import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { useGetUserData } from "../../hooks/useGetUserData";
import AIAssistant from "../AiChat/AIAssistant";
import { NetworkStatusAlert, NetworkStatusBadge } from "../NetworkBadge/NetworkBadge";
import { useIsMobile } from "../../hooks/use-mobile";

const pageTitles = {
    "/": "Dashboard",
    "/dashboard": "Dashboard",
    "/insights": "Insights",
    "/clinics": "Clinics Management", 
    "/clinics/new": "Add New Clinic",
    "/clinics/edit": "Edit Clinic",
    "/schedule": "Schedule",
    "/availability": "Availability Hours",
    "/form-builder": "Form Builder",
    "/patients": "Patients",
    "/bookings": "Bookings Management",
    "/Services": "Services",
    "/blogs": "Blogs",
    "/packages": "Packages",
    "/sub": "Subscription",
};

const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [aiChatOpen, setAiChatOpen] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const { data, isLoading } = useGetUserData();
    const location = useLocation();
    const isMobile = useIsMobile();
const role=localStorage.getItem("currentRole")
    const getPageTitle = (path) => {
        if (pageTitles[path]) return pageTitles[path];
        
        if (path.includes('/edit/')) return "Edit";
        if (path.includes('/view/')) return "View Details";
        if (path.includes('/new')) return "Add New";
        
        const basePath = '/' + path.split('/')[1];
        if (pageTitles[basePath]) return pageTitles[basePath];
        
        return (
            <img 
                src="https://res.cloudinary.com/dk2uh7sho/image/upload/v1752771874/dreasy_1_n6ygy8.png" 
                className="w-20 sm:w-24 lg:w-28" 
                alt="DrEasy Logo"
            />
        );
    };

    const pageTitle = getPageTitle(location.pathname);

    useEffect(() => {
        const initializeSidebar = () => {
            if (!isInitialized) {
                const isDesktop = window.innerWidth >= 768;
                setSidebarOpen(isDesktop);
                setIsInitialized(true);
            }
        };

        initializeSidebar();
    }, [isInitialized]);

    useEffect(() => {
        if (!isInitialized) return;

        const handleResize = () => {
            if (window.innerWidth >= 768) { 
                setSidebarOpen(true); 
            } else {
                setSidebarOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isInitialized]);

    useEffect(() => {
        if (isMobile && sidebarOpen) {
            setSidebarOpen(false);
        }
    }, [location.pathname, isMobile]);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    if (!isInitialized) {
        return <div className="min-h-screen bg-gray-50" />; 
    }

    return (
        <div className="min-h-screen bg-gray-50 flex">
            <Sidebar
                isOpen={sidebarOpen}
                onToggle={toggleSidebar}
                user={data}
                isLoading={isLoading}
                role={role}
            />

            <div className={`flex-1 flex flex-col transition-all duration-300 min-w-0
                ${isMobile ? 'ml-0' : sidebarOpen ? 'ml-64' : 'ml-16'}`}>
                
                <Header 
                    user={data} 
                    isLoading={isLoading} 
                    toggleSidebar={toggleSidebar} 
                    title={pageTitle} 
                    role={role}
                />
                
                {/* Network Status Alert */}
                <NetworkStatusAlert />

                {/* Main Content */}
                <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
                    <div className="max-w-full">
                        <Outlet />
                        {children}
                    </div>
                </main>

                {/* Bottom padding for mobile navigation if needed */}
                <div className="h-16 sm:h-0" />
            </div>

            {/* AI Assistant Chat */}
            <AIAssistant 
                isOpen={aiChatOpen} 
                onToggle={() => setAiChatOpen(!aiChatOpen)}
                user={data}
                
            />
        </div>
    );
};

export default Layout;